import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/shared/hooks/use-toast';
import { upsertUserProfileFromAuth } from '@/lib/authHelpers';
import { apiClient } from '@/shared/services/api';
import { API_ROUTES } from '@/shared/services/apiRoutes';

export default function OAuthCallbackPage() {
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        console.log('[OAuthCallback] Starting OAuth callback handler...');
        console.log('[OAuthCallback] Full URL:', window.location.href);

        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        console.log('[OAuthCallback] URL parameters:', {
          hasAuthCode: !!authCode,
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken
        });

        // ✅ NEW: Direct Google OAuth flow (authorization code)
        if (authCode) {
          console.log('[OAuthCallback] Processing authorization code flow...');

          const REDIRECT_URI = `${window.location.origin}/auth/callback`;
          const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

          // Exchange authorization code for ID token via backend
          const tokenResponse = await fetch(`${API_URL}/api/auth/google-oauth-exchange`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              code: authCode,
              redirectUri: REDIRECT_URI,
            }),
          });

          if (!tokenResponse.ok) {
            const errorData = await tokenResponse.json().catch(() => ({}));
            console.error('[OAuthCallback] Token exchange failed:', errorData);
            throw new Error(errorData.error || 'Failed to exchange authorization code');
          }

          const tokenData = await tokenResponse.json();
          console.log('[OAuthCallback] Got ID token from backend');

          if (!tokenData.ok || !tokenData.id_token) {
            throw new Error('Invalid response from backend');
          }

          // Sign in to Supabase using Google ID token (AppLoadingGate will show unified loading)
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: tokenData.id_token,
          });

          if (error) {
            console.error('[OAuthCallback] signInWithIdToken error:', error);
            toast({
              title: 'Sign-in Failed',
              description: error.message || 'Failed to create session.',
              variant: 'destructive',
            });
            navigate('/auth', { replace: true });
            return;
          }

          if (!data.session) {
            console.error('[OAuthCallback] No session after signInWithIdToken');
            toast({
              title: 'Sign-in Failed',
              description: 'Could not create session. Please try again.',
              variant: 'destructive',
            });
            navigate('/auth', { replace: true });
            return;
          }

          console.log('[OAuthCallback] ✅ Session created successfully:', data.session.user.id);
          await processOAuthLogin(data.session);
          return;
        }

        // ✅ OLD: Supabase OAuth flow (hash fragment tokens) - for backward compatibility
        if (accessToken && refreshToken) {
          console.log('[OAuthCallback] Processing Supabase OAuth flow...');

          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('[OAuthCallback] setSession error:', error);
            toast({
              title: 'Sign-in Failed',
              description: error.message || 'Failed to create session.',
              variant: 'destructive',
            });
            navigate('/auth', { replace: true });
            return;
          }

          if (!data.session) {
            console.error('[OAuthCallback] No session after setSession');
            toast({
              title: 'Sign-in Failed',
              description: 'Could not create session. Please try again.',
              variant: 'destructive',
            });
            navigate('/auth', { replace: true });
            return;
          }

          console.log('[OAuthCallback] ✅ Session set successfully:', data.session.user.id);
          await processOAuthLogin(data.session);
          return;
        }

        // No valid OAuth parameters found
        console.error('[OAuthCallback] No authorization code or tokens in URL');
        toast({
          title: 'Sign-in Failed',
          description: 'No authentication data received.',
          variant: 'destructive',
        });
        navigate('/auth', { replace: true });
      } catch (err: any) {
        console.error('[OAuthCallback] Unexpected error:', err);
        toast({
          title: 'Sign-in Error',
          description: err.message || 'An unexpected error occurred.',
          variant: 'destructive',
        });
        navigate('/auth', { replace: true });
      }
    };

    const processOAuthLogin = async (session: any) => {
      try {
        const user = session.user;
        console.log('[OAuthCallback] User authenticated:', user.id, user.email);

        // ✅ FIX: Use backend API instead of direct Supabase query
        // This ensures proper profile creation and avoids RLS policy issues
        try {
          // Upsert basic profile from OAuth data using backend API
          console.log('[OAuthCallback] Upserting profile via backend API...');
          await upsertUserProfileFromAuth();

          // Check profile status via backend API
          console.log('[OAuthCallback] Fetching user data via backend API...');
          const userData = await apiClient.get<any>(API_ROUTES.USER_ME);

          const user = userData?.user;
          console.log('[OAuthCallback] Profile status:', {
            hasUser: !!user,
            profile_setup_complete: user?.profile_setup_complete,
            hasCompany: !!userData?.company,
            companyId: userData?.company?.id,
            hasPersonalDetails: !!(user?.first_name && user?.last_name && user?.phone)
          });

          // Check if profile is TRULY complete (not just auto-created by trigger)
          // Must have: profile_setup_complete=true, company exists, AND personal details filled
          const isProfileComplete = user?.profile_setup_complete &&
                                    userData?.company &&
                                    user?.first_name &&
                                    user?.last_name &&
                                    user?.phone;

          // If profile not complete, go to profile setup
          if (!isProfileComplete) {
            console.log('[OAuthCallback] Profile incomplete - redirecting to profile-setup');
            navigate('/profile-setup', { replace: true });
            return;
          }

          // Profile complete - clear any pending flags and go to dashboard
          console.log('[OAuthCallback] ✅ Profile complete - redirecting to dashboard');
          localStorage.removeItem('pendingTrialCheckout');
          localStorage.removeItem('pendingOAuthSetup');
          navigate('/dashboard', { replace: true });

        } catch (apiError: any) {
          console.error('[OAuthCallback] ❌ Backend API error:', apiError);

          // ✅ SAFE FALLBACK: Always redirect to profile-setup if we can't verify status
          // ProfileSetupPage will redirect to dashboard if profile is actually complete
          console.log('[OAuthCallback] Cannot verify profile status - redirecting to profile-setup for safety');

          toast({
            title: 'Sign-in successful',
            description: 'Please complete your profile setup.',
          });

          navigate('/profile-setup', { replace: true });
          return;
        }
      } catch (err: any) {
        console.error('[OAuthCallback] processOAuthLogin error:', err);

        // ✅ SAFE FALLBACK: Redirect to profile-setup on any unexpected error
        toast({
          title: 'Sign-in successful',
          description: 'Please complete your profile setup.',
        });

        navigate('/profile-setup', { replace: true });
      }
    };

    handleOAuthCallback();
  }, [navigate, toast]);

  // Return invisible component - AppLoadingGate will show unified "Loading wlkthru" screen
  return null;
}
